import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CATEGORIES, fetchProducts } from "@/lib/products";
import { ProductCard } from "@/components/site/ProductCard";
import { MonogramLoader } from "@/components/site/MonogramLoader";

export const Route = createFileRoute("/shop")({
  head: () => ({
    meta: [
      { title: "Shop the Collection — KAKAIYS" },
      {
        name: "description",
        content:
          "Browse the full KAKAIYS collection of hand-finished leather jackets, bags, wallets and belts.",
      },
      { property: "og:title", content: "Shop the Collection — KAKAIYS" },
      {
        property: "og:description",
        content: "Hand-finished leather jackets, bags, wallets and belts.",
      },
      { property: "og:url", content: "https://kakaiys.com/shop" },
    ],
    links: [{ rel: "canonical", href: "https://kakaiys.com/shop" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Shop the Collection — KAKAIYS",
          description:
            "The full KAKAIYS collection of hand-finished leather jackets, bags, wallets and belts.",
          url: "https://kakaiys.com/shop",
          isPartOf: { "@id": "https://kakaiys.com/#website" },
          publisher: { "@id": "https://kakaiys.com/#organization" },
        }),
      },
    ],
  }),
  component: Shop,
});

function Shop() {
  const [filter, setFilter] = useState<string>("All");
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", "active"],
    queryFn: () => fetchProducts(),
  });
  const list = filter === "All" ? products : products.filter((p) => p.category === filter);

  // Editorial chunks of 6: [2 wide, 4 standard]
  const chunks: { wide: typeof list; grid: typeof list }[] = [];
  for (let i = 0; i < list.length; i += 6) {
    chunks.push({ wide: list.slice(i, i + 2), grid: list.slice(i + 2, i + 6) });
  }

  let idx = 0;

  return (
    <>
      <section className="pt-36 md:pt-48 pb-16 md:pb-20 container-luxe text-center">
        <div className="eyebrow mb-5 text-gold">The Collection</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] tracking-tight leading-[1.05]">
          Every Piece, <span className="italic text-gradient-gold text-slate-50">Made to Last.</span>
        </h1>
        <p className="mt-7 max-w-lg mx-auto text-sm md:text-base text-muted-foreground leading-relaxed font-light">
          {products.length} pieces in the current collection. Each one finished by hand in our
          Sialkot atelier.
        </p>
      </section>

      <section className="sticky top-16 md:top-20 z-30 bg-background/90 backdrop-blur-md border-y border-border">
        <div className="container-luxe py-5">
          <div className="flex flex-wrap gap-x-8 gap-y-2 justify-center text-[10px] uppercase tracking-[0.32em]">
            {(["All", ...CATEGORIES] as const).map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`pb-1 border-b transition-colors ${
                  filter === c
                    ? "border-gold text-gold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="container-luxe py-20 md:py-28">
        {isLoading ? (
          <MonogramLoader />
        ) : list.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-32 font-light tracking-wide">
            No pieces in this category yet.
          </div>
        ) : (
          <div className="space-y-20 md:space-y-28">
            {chunks.map((chunk, ci) => (
              <div key={ci} className="space-y-20 md:space-y-28">
                {chunk.wide.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-10 gap-y-16">
                    {chunk.wide.map((p) => {
                      const i = idx++;
                      return (
                        <ProductCard
                          key={p.slug}
                          product={p}
                          index={i}
                          eager={ci === 0 && i < 2}
                        />
                      );
                    })}
                  </div>
                )}
                {chunk.grid.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 md:gap-x-8 gap-y-16">
                    {chunk.grid.map((p) => {
                      const i = idx++;
                      return <ProductCard key={p.slug} product={p} index={i} />;
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
